import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanies } from "../../../hooks/useCompanyData";

interface SidebarCompanyScrollerProps {
  isKr: boolean;
}

const SidebarCompanyScroller = ({ isKr }: SidebarCompanyScrollerProps) => {
  const navigate = useNavigate();
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const posRef = useRef(0);
  const { companies: rawCompanies, loading } = useCompanies();

  const companies = rawCompanies.map((c) => ({
    id: c.id,
    name: isKr ? c.nameKr : c.name,
    logoUrl: c.logoUrl,
    logoFrom: c.logoFrom,
    logoTo: c.logoTo,
    logoIcon: c.logoIcon,
    logoInitials: c.logoInitials,
    category: isKr ? c.mainCategoryNameKr : c.mainCategoryName,
  }));

  useEffect(() => {
    const el = tickerRef.current;
    if (!el || loading || companies.length === 0) return;
    const SPEED = 0.4;
    const animate = () => {
      if (!isPaused && el) {
        posRef.current += SPEED;
        const halfH = el.scrollHeight / 2;
        if (posRef.current >= halfH) posRef.current = 0;
        el.style.transform = `translateY(-${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isPaused, loading, companies.length]);

  const doubled = [...companies, ...companies];

  return (
    <div className="h-full flex flex-col bg-white/80 backdrop-blur-sm border-l border-gray-100/80">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003d7d] to-[#004A99] px-3 py-3 text-center flex-shrink-0">
        <p className="text-white text-[11px] font-bold tracking-wider uppercase whitespace-nowrap">
          {isKr ? "파트너 기업" : "ĐỐI TÁC"}
        </p>
        <p className="text-white/60 text-[10px] mt-0.5">
          {companies.length} {isKr ? "기업" : "doanh nghiệp"}
        </p>
      </div>

      {/* Scrolling logos */}
      <div
        className="flex-1 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {loading ? (
          <div className="flex flex-col gap-2 p-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse flex flex-col items-center py-3">
                <div className="w-20 h-20 bg-gray-100 rounded-xl mb-2" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div ref={tickerRef} className="will-change-transform">
            {doubled.map((company, idx) => (
              <div
                key={`${company.id}-${idx}`}
                className="flex flex-col items-end pr-3 pl-2 py-4 cursor-pointer hover:bg-[#004A99]/5 transition-colors group border-b border-gray-50"
                onClick={() => navigate(`/doanh-nghiep/${company.id}`)}
              >
                {/* Logo — centered within right-aligned block */}
                <div className="w-full flex justify-center mb-2.5">
                  <div className="w-[110px] h-[110px] overflow-hidden flex-shrink-0 bg-white flex items-center justify-center">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex flex-col items-center justify-center rounded-xl"
                        style={{ background: `linear-gradient(135deg, ${company.logoFrom}, ${company.logoTo})` }}
                      >
                        <i className={`${company.logoIcon} text-white text-3xl`} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Company name */}
                <p className="text-[11px] font-semibold text-gray-700 text-center line-clamp-2 leading-tight group-hover:text-[#004A99] transition-colors w-full">
                  {company.name}
                </p>

                {/* Category badge */}
                <span className="mt-1 text-[10px] text-gray-400 text-center line-clamp-1 w-full">
                  {company.category}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarCompanyScroller;
